import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../hooks/useAuth';
import Button from '../../components/common/Button';
import api from '../../services/api';
import styles from './RolesPage.module.css';

// ── Pure helpers over the manifest `requires` graph ──────────────────────────
const collectParents = (code, requiresMap, acc = new Set()) => {
  for (const parent of requiresMap.get(code) || []) if (!acc.has(parent)) { acc.add(parent); collectParents(parent, requiresMap, acc); }
  return acc;
};
const collectChildren = (code, childrenMap, acc = new Set()) => {
  for (const child of childrenMap.get(code) || []) if (!acc.has(child)) { acc.add(child); collectChildren(child, childrenMap, acc); }
  return acc;
};
// Enabling a code auto-includes every parent it requires; disabling clears every dependent child.
const addWithParents = (selected, code, requiresMap) => new Set([...selected, code, ...collectParents(code, requiresMap)]);
const removeWithChildren = (selected, code, childrenMap) => {
  const drop = new Set([code, ...collectChildren(code, childrenMap)]);
  return new Set([...selected].filter((c) => !drop.has(c)));
};

const ChevronIcon = ({ collapsed }) => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}><polyline points="6 9 12 15 18 9" /></svg>
);
const LockIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0110 0v4" /></svg>
);

/**
 * Permission editor for one role. Driven by GET /admin/permissions/manifest
 * (structure + requires + superAdminOnly) merged with GET /admin/roles/:id/permissions
 * (code→id map + current grants). Holds a Set<code>; converts to permissionIds on save.
 * The server (Task 5) enforces parent/child, subset & superAdminOnly — this UI prevents
 * violations up front (auto-include parents, disable un-grantable toggles, hide
 * superAdminOnly for custom roles) and renders any server rejection inline.
 */
export default function RoleEditor({ role, canEdit, onSaved }) {
  const { toast } = useToast();
  const { hasPermission } = useAuth();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [groups, setGroups] = useState([]);
  const [codeToId, setCodeToId] = useState(new Map());
  const [requiresMap, setRequiresMap] = useState(new Map());
  const [childrenMap, setChildrenMap] = useState(new Map());
  const [selected, setSelected] = useState(new Set());
  const [initial, setInitial] = useState(new Set());
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [justSaved, setJustSaved] = useState(false);
  const [flash, setFlash] = useState(new Set());
  const reqRef = useRef(0);
  const flashTimer = useRef(null);

  const isSystem = !!role?.isSystem;
  const editable = canEdit && !isSystem;

  const load = useCallback(async () => {
    const myReq = ++reqRef.current;
    setLoading(true); setLoadError(null);
    try {
      const [manifest, rolePerms] = await Promise.all([
        api.get('/admin/permissions/manifest'),
        api.get(`/admin/roles/${role.id}/permissions`)
      ]);
      if (myReq !== reqRef.current) return; // superseded by a newer role selection
      const idByCode = new Map((rolePerms.permissions || []).map((p) => [p.code, p.id]));
      const grantedCodes = new Set((rolePerms.permissions || []).filter((p) => p.granted).map((p) => p.code));
      const reqMap = new Map(); const childMap = new Map();
      for (const g of manifest.groups || []) for (const m of g.modules) for (const p of m.permissions) {
        reqMap.set(p.code, p.requires || []);
        for (const parent of p.requires || []) { if (!childMap.has(parent)) childMap.set(parent, []); childMap.get(parent).push(p.code); }
      }
      // Custom roles can never hold superAdminOnly keys — hide them entirely.
      const visibleGroups = (manifest.groups || []).map((g) => ({
        group: g.group,
        modules: g.modules.map((m) => ({ module: m.module, label: m.label, permissions: m.permissions.filter((p) => isSystem || !p.superAdminOnly) })).filter((m) => m.permissions.length > 0)
      })).filter((g) => g.modules.length > 0);
      const visibleCodes = new Set();
      visibleGroups.forEach((g) => g.modules.forEach((m) => m.permissions.forEach((p) => visibleCodes.add(p.code))));
      const init = new Set([...grantedCodes].filter((c) => visibleCodes.has(c)));
      setGroups(visibleGroups); setCodeToId(idByCode); setRequiresMap(reqMap); setChildrenMap(childMap);
      setSelected(init); setInitial(init); setSaveError(null); setJustSaved(false);
    } catch (err) {
      if (myReq === reqRef.current) setLoadError(err.message || 'Failed to load permissions');
    } finally {
      if (myReq === reqRef.current) setLoading(false);
    }
  }, [role?.id, isSystem]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => () => { if (flashTimer.current) clearTimeout(flashTimer.current); }, []);

  // One-shot highlight of codes the last change added/removed (parent-include / child-clear cue).
  // Never a perpetual animation — an infinite one hangs headless-browser clicks.
  const flashChanged = (before, after) => {
    const changed = new Set([...after].filter((c) => !before.has(c)).concat([...before].filter((c) => !after.has(c))));
    if (changed.size === 0) return;
    setFlash(changed);
    if (flashTimer.current) clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setFlash(new Set()), 1300);
  };
  const clearStatus = () => { setJustSaved(false); setSaveError(null); };

  const toggleCode = (code) => {
    if (!editable || !hasPermission(code)) return;
    setSelected((prev) => { const next = prev.has(code) ? removeWithChildren(prev, code, childrenMap) : addWithParents(prev, code, requiresMap); flashChanged(prev, next); return next; });
    clearStatus();
  };
  const toggleModule = (perms) => {
    if (!editable) return;
    const grantable = perms.filter((p) => hasPermission(p.code));
    const allOn = grantable.length > 0 && grantable.every((p) => selected.has(p.code));
    setSelected((prev) => {
      let next = prev;
      grantable.forEach((p) => { next = allOn ? removeWithChildren(next, p.code, childrenMap) : addWithParents(next, p.code, requiresMap); });
      flashChanged(prev, next); return next;
    });
    clearStatus();
  };
  const toggleCollapse = (key) => setCollapsed((prev) => { const n = new Set(prev); if (n.has(key)) n.delete(key); else n.add(key); return n; });

  const dirty = useMemo(() => {
    if (selected.size !== initial.size) return true;
    for (const c of selected) if (!initial.has(c)) return true;
    return false;
  }, [selected, initial]);
  const totalVisible = useMemo(() => { let n = 0; groups.forEach((g) => g.modules.forEach((m) => { n += m.permissions.length; })); return n; }, [groups]);

  const term = search.trim().toLowerCase();
  const filteredGroups = useMemo(() => {
    if (!term) return groups;
    return groups.map((g) => ({
      group: g.group,
      modules: g.modules.map((m) => ({ ...m, permissions: m.permissions.filter((p) => p.code.toLowerCase().includes(term) || p.label.toLowerCase().includes(term) || m.label.toLowerCase().includes(term)) })).filter((m) => m.permissions.length > 0)
    })).filter((g) => g.modules.length > 0);
  }, [groups, term]);

  const handleSave = async () => {
    setSaving(true); setSaveError(null);
    try {
      const permissionIds = [...selected].map((c) => codeToId.get(c)).filter(Boolean);
      await api.put(`/admin/roles/${role.id}/permissions`, { permissionIds });
      setInitial(new Set(selected)); setJustSaved(true); toast.success('Permissions saved'); onSaved?.();
    } catch (err) {
      const msg = err.message || 'Failed to save permissions'; setSaveError(msg); toast.error(msg);
    } finally { setSaving(false); }
  };
  const handleCancel = () => { setSelected(new Set(initial)); clearStatus(); };

  if (loading) return (
    <div className={styles.permissionsEditor}>
      <div className={styles.editorSkeleton} aria-busy="true" aria-label="Loading permissions">
        {Array.from({ length: 7 }).map((_, i) => <div key={i} className={styles.skeletonBar} />)}
      </div>
    </div>
  );
  if (loadError) return (
    <div className={styles.permissionsEditor}>
      <div className={styles.editorError}><p>{loadError}</p><Button size="small" variant="secondary" onClick={load}>Retry</Button></div>
    </div>
  );

  const renderModule = (m) => {
    const selCount = m.permissions.filter((p) => selected.has(p.code)).length;
    const grantableCount = m.permissions.filter((p) => hasPermission(p.code)).length;
    const allOn = m.permissions.length > 0 && selCount === m.permissions.length;
    const someOn = selCount > 0 && !allOn;
    return (
      <div key={m.module} className={styles.moduleBlock}>
        <div className={styles.moduleHead}>
          <label className={styles.moduleCheck}>
            <input type="checkbox" checked={isSystem ? true : allOn} disabled={!editable || grantableCount === 0} ref={(el) => { if (el) el.indeterminate = !isSystem && someOn; }} onChange={() => toggleModule(m.permissions)} />
            <span className={styles.moduleLabel}>{m.label}</span>
          </label>
          <span className={styles.moduleCount}>{selCount}/{m.permissions.length}</span>
        </div>
        <div className={styles.permRows}>
          {m.permissions.map((p) => {
            const canGrant = isSystem || hasPermission(p.code);
            const disabled = !editable || !canGrant;
            const tip = !canGrant && !isSystem ? "You can't grant a permission you don't hold." : (p.description || '');
            return (
              <div key={p.code} className={`${styles.permRow} ${flash.has(p.code) ? styles.permFlash : ''}`}>
                <label className={`${styles.toggle} ${disabled ? styles.toggleDisabled : ''}`} title={tip}>
                  <input type="checkbox" checked={isSystem ? true : selected.has(p.code)} disabled={disabled} aria-label={p.label} onChange={() => toggleCode(p.code)} />
                  <span className={styles.slider} />
                </label>
                <div className={styles.permText}><span className={styles.permLabel}>{p.label}</span><span className={styles.permCode}>{p.code}</span></div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className={styles.permissionsEditor}>
      <div className={styles.editorToolbar}>
        <input className={styles.searchInput} type="text" placeholder="Search permissions by name or key…" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Search permissions" />
        <span className={styles.permCountBadge}>{selected.size} / {totalVisible} selected</span>
      </div>

      {isSystem && <div className={styles.systemNotice}><LockIcon /> System role &mdash; every permission is granted and locked. It cannot be edited.</div>}
      {!isSystem && !canEdit && <div className={styles.systemNotice}>You have read-only access to this role&rsquo;s permissions.</div>}

      {saveError && <div className={styles.saveError}>{saveError}</div>}
      {justSaved && !dirty && <div className={styles.saveSuccess}>Permissions saved successfully.</div>}

      <div className={styles.permissionsSections}>
        {filteredGroups.map((group) => {
          const isCollapsed = collapsed.has(group.group) && !term;
          return (
            <div key={group.group} className={styles.permissionSection}>
              <div className={styles.sectionHeader} onClick={() => toggleCollapse(group.group)}>
                <div className={styles.sectionHeaderLeft}>
                  <ChevronIcon collapsed={isCollapsed} />
                  <h3 className={styles.sectionTitle}>{group.group}</h3>
                  <span className={styles.sectionModuleCount}>{group.modules.length} modules</span>
                </div>
              </div>
              {!isCollapsed && <div className={styles.moduleList}>{group.modules.map(renderModule)}</div>}
            </div>
          );
        })}
        {filteredGroups.length === 0 && <div className={styles.emptySearch}>No permissions match &ldquo;{search}&rdquo;.</div>}
      </div>

      {editable && (
        <div className={styles.editorActions}>
          <Button variant="secondary" size="small" onClick={handleCancel} disabled={!dirty || saving}>Cancel</Button>
          <Button size="small" loading={saving} disabled={!dirty} onClick={handleSave}>Save Changes</Button>
        </div>
      )}
    </div>
  );
}
